(() => {
  if (window.gumodePromoCarouselLoaded) return;
  window.gumodePromoCarouselLoaded = true;

  const carouselSelector = '[data-promo-carousel]';
  const instagramCouponTriggerSelector = '[data-product-instagram-coupon-trigger]';
  const carouselNavButtonSelector = '[data-promo-carousel-prev], [data-promo-carousel-next]';
  const controllers = new Map();

  const queryCarousels = (scope = document) => {
    if (!scope) return [];

    return scope.matches?.(carouselSelector)
      ? [scope]
      : Array.from(scope.querySelectorAll(carouselSelector));
  };

  const destroyCarousel = (carousel) => {
    const controller = controllers.get(carousel);
    if (!controller) return;

    controller.destroy();
    controllers.delete(carousel);
  };

  const initCarousel = (carouselContainer) => {
    if (!carouselContainer || controllers.has(carouselContainer)) return;

    const track = carouselContainer.querySelector('[data-promo-carousel-track]');
    const previousButton = carouselContainer.querySelector('[data-promo-carousel-prev]');
    const nextButton = carouselContainer.querySelector('[data-promo-carousel-next]');
    const originalSlides = Array.from(track?.querySelectorAll('.gumode-slide') || []);

    if (!track || originalSlides.length === 0) return;

    const cleanupFns = [];
    const originalMarkup = track.innerHTML;
    const originalCount = originalSlides.length;
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const autoplayEnabled = carouselContainer.dataset.promoAutoplay !== 'false';
    const autoplaySpeed = Number(carouselContainer.dataset.promoAutoplaySpeed) || 4000;

    let currentIndex = 1;
    let interval = null;
    let isTransitioning = false;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let moved = false;
    let activePointerId = null;
    let pressTarget = null;
    let lastCouponOpenedAt = 0;
    const couponTapTolerance = 16;

    const addListener = (target, eventName, handler, options) => {
      if (!target) return;

      target.addEventListener(eventName, handler, options);
      cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    const isCarouselNavButton = (target) => Boolean(target?.closest?.(carouselNavButtonSelector));
    const getInstagramCouponTrigger = (target) => target?.closest?.(instagramCouponTriggerSelector);

    const openInstagramCouponDrawer = (trigger) => {
      if (!trigger) return false;

      const now = Date.now();
      if (now - lastCouponOpenedAt < 500) return true;

      const sectionId = trigger.dataset.instagramCouponSectionId || carouselContainer.dataset.instagramCouponSectionId;
      if (!sectionId) return false;

      lastCouponOpenedAt = now;
      document.dispatchEvent(new CustomEvent('gm:instagram-coupon:open', {
        detail: {
          sectionId
        }
      }));
      return true;
    };

    const openPressedInstagramCouponTrigger = () => {
      const trigger = getInstagramCouponTrigger(pressTarget);
      pressTarget = null;
      return openInstagramCouponDrawer(trigger);
    };

    if (originalCount <= 1) {
      controllers.set(carouselContainer, {
        destroy() {
          delete carouselContainer.promoCarousel;
        }
      });
      return;
    }

    const firstClone = originalSlides[0].cloneNode(true);
    const lastClone = originalSlides[originalCount - 1].cloneNode(true);
    firstClone.dataset.clone = 'true';
    lastClone.dataset.clone = 'true';

    track.insertBefore(lastClone, originalSlides[0]);
    track.appendChild(firstClone);

    const setTrackPosition = (index, withTransition = true) => {
      track.style.transition = withTransition ? 'transform 0.5s ease-in-out' : 'none';
      track.style.transform = `translateX(-${index * 100}%)`;
    };

    const clearTimer = () => {
      if (!interval) return;
      window.clearInterval(interval);
      interval = null;
    };

    const startTimer = () => {
      if (prefersReducedMotion || !autoplayEnabled || document.hidden) return;

      clearTimer();
      interval = window.setInterval(() => goToSlide(currentIndex + 1), autoplaySpeed);
    };

    const resetTimer = () => {
      clearTimer();
      startTimer();
    };

    const goToSlide = (index) => {
      if (isTransitioning) return;

      isTransitioning = true;
      currentIndex = index;
      setTrackPosition(currentIndex, true);
      resetTimer();
    };

    const goToPreviousSlide = () => goToSlide(currentIndex - 1);
    const goToNextSlide = () => goToSlide(currentIndex + 1);

    carouselContainer.promoCarousel = {
      goToSlide
    };

    addListener(previousButton, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      goToPreviousSlide();
    });

    addListener(nextButton, 'click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      goToNextSlide();
    });

    addListener(track, 'transitionend', () => {
      if (currentIndex === originalCount + 1) {
        currentIndex = 1;
        setTrackPosition(currentIndex, false);
      } else if (currentIndex === 0) {
        currentIndex = originalCount;
        setTrackPosition(currentIndex, false);
      }

      isTransitioning = false;
    });

    addListener(
      carouselContainer,
      'touchstart',
      (event) => {
        if (isTransitioning) return;
        if (isCarouselNavButton(event.target)) return;

        startX = event.touches[0].clientX;
        currentX = startX;
        isDragging = true;
        moved = false;
        pressTarget = event.target;
        clearTimer();
        track.style.transition = 'none';
      },
      { passive: true }
    );

    addListener(
      carouselContainer,
      'touchmove',
      (event) => {
        if (!isDragging) return;

        currentX = event.touches[0].clientX;
        const diff = currentX - startX;
        if (Math.abs(diff) > 5) moved = true;

        const baseTranslate = -(currentIndex * carouselContainer.offsetWidth);
        track.style.transform = `translateX(${baseTranslate + diff}px)`;
      },
      { passive: true }
    );

    addListener(carouselContainer, 'touchend', () => {
      if (!isDragging) return;

      isDragging = false;
      const diff = currentX - startX;
      const threshold = 50;

      if (diff < -threshold) {
        currentIndex += 1;
        isTransitioning = true;
      } else if (diff > threshold) {
        currentIndex -= 1;
        isTransitioning = true;
      }

      if (Math.abs(diff) <= couponTapTolerance && openPressedInstagramCouponTrigger()) {
        setTrackPosition(currentIndex, true);
        resetTimer();
        return;
      }

      pressTarget = null;
      setTrackPosition(currentIndex, true);
      resetTimer();
    });

    addListener(carouselContainer, 'pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || isTransitioning) return;
      if (isCarouselNavButton(event.target)) return;

      activePointerId = event.pointerId;
      startX = event.clientX;
      currentX = startX;
      isDragging = true;
      moved = false;
      pressTarget = event.target;
      clearTimer();
      track.style.transition = 'none';
      carouselContainer.classList.add('is-dragging');
      carouselContainer.setPointerCapture?.(activePointerId);
      event.preventDefault();
    });

    addListener(carouselContainer, 'pointermove', (event) => {
      if (!isDragging || event.pointerType !== 'mouse' || event.pointerId !== activePointerId) return;

      currentX = event.clientX;
      const diff = currentX - startX;
      if (Math.abs(diff) > 5) moved = true;

      const baseTranslate = -(currentIndex * carouselContainer.offsetWidth);
      track.style.transform = `translateX(${baseTranslate + diff}px)`;
    });

    const stopPointerDrag = (event) => {
      if (!isDragging || event.pointerType !== 'mouse' || event.pointerId !== activePointerId) return;

      isDragging = false;
      const diff = currentX - startX;
      const threshold = 50;

      if (diff < -threshold) {
        currentIndex += 1;
        isTransitioning = true;
      } else if (diff > threshold) {
        currentIndex -= 1;
        isTransitioning = true;
      }

      carouselContainer.releasePointerCapture?.(activePointerId);
      activePointerId = null;
      carouselContainer.classList.remove('is-dragging');

      if (Math.abs(diff) <= couponTapTolerance && openPressedInstagramCouponTrigger()) {
        setTrackPosition(currentIndex, true);
        resetTimer();
        return;
      }

      pressTarget = null;
      setTrackPosition(currentIndex, true);
      resetTimer();
    };

    addListener(carouselContainer, 'pointerup', stopPointerDrag);
    addListener(carouselContainer, 'pointercancel', stopPointerDrag);

    addListener(
      carouselContainer,
      'click',
      (event) => {
        if (isCarouselNavButton(event.target)) return;

        if (!moved) {
          const trigger = getInstagramCouponTrigger(event.target);
          if (!openInstagramCouponDrawer(trigger)) return;

          event.preventDefault();
          event.stopPropagation();
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );

    addListener(document, 'visibilitychange', () => {
      if (document.hidden) {
        clearTimer();
        return;
      }

      startTimer();
    });

    setTrackPosition(currentIndex, false);
    startTimer();

    controllers.set(carouselContainer, {
      destroy() {
        clearTimer();
        cleanupFns.forEach((cleanup) => cleanup());
        cleanupFns.length = 0;
        track.innerHTML = originalMarkup;
        delete carouselContainer.promoCarousel;
      }
    });
  };

  const initAll = (scope = document) => {
    queryCarousels(scope).forEach(initCarousel);
  };

  const destroyAll = (scope = document) => {
    queryCarousels(scope).forEach(destroyCarousel);
  };

  if (document.readyState !== 'loading') {
    initAll();
  } else {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target?.closest?.('.gumode-slide');
    if (!slide) return;

    const carousel = slide.closest(carouselSelector);
    const track = carousel?.querySelector('[data-promo-carousel-track]');
    if (!carousel || !track || !carousel.promoCarousel) return;

    const allSlides = Array.from(track.children);
    const targetIndex = allSlides.indexOf(slide);

    if (targetIndex < 0) return;

    carousel.promoCarousel.goToSlide(targetIndex);
  });
})();
